/**
 * User Settings Context
 * Provides a centralized state for user settings to avoid desynchronization
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getOrCreateUserSettings } from '../user-settings';
import useUser from '../hooks/use-user';

interface UserSettingsContextType {
    settings: UserSettings | null;
    isLoading: boolean;
    needsOnboarding: boolean;
    refresh: () => Promise<void>;
}

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

export function UserSettingsProvider({ children }: { children: React.ReactNode }) {
    const { user } = useUser();
    const [settings, setSettings] = useState<UserSettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const loadSettings = useCallback(async () => {
        if (!user) {
            setSettings(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const userSettings = await getOrCreateUserSettings(user);
            setSettings(userSettings);
        } catch (error) {
            console.error('Error loading user settings in context:', error);
            setSettings(null);
        } finally {
            setIsLoading(false);
        }
    }, [user?.uid]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const needsOnboarding = !isLoading && settings !== null && !settings.hasCompletedOnboarding;

    const value = {
        settings,
        isLoading,
        needsOnboarding,
        refresh: loadSettings,
    };

    return (
        <UserSettingsContext.Provider value={value}>
            {children}
        </UserSettingsContext.Provider>
    );
}

export function useUserSettingsContext() {
    const context = useContext(UserSettingsContext);
    if (context === undefined) {
        throw new Error('useUserSettingsContext must be used within a UserSettingsProvider');
    }
    return context;
}
